import { useEffect, useRef, useMemo } from 'react';
import { Renderer, Program, Mesh, Triangle, Texture } from 'ogl';
import './PrismaticBurst.css';

/**
 * ULTRA-LIGHTWEIGHT SHADER SYSTEM
 * - No Raymarching
 * - 2D Animated Gradients
 * - Zero Lag on Mobile/Integrated GPUs
 */

const vertexShader = `#version 300 es
in vec2 position;
in vec2 uv;
out vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `#version 300 es
precision highp float;

out vec4 fragColor;

uniform vec2  uResolution;
uniform float uTime;
uniform float uIntensity;
uniform float uSpeed;
uniform int   uAnimType;
uniform vec2  uMouse;
uniform int   uColorCount;
uniform float uDistort;
uniform sampler2D uGradient;

float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
               mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

vec3 getCol(float t) {
    return texture(uGradient, vec2(clamp(t, 0.0, 1.0), 0.5)).rgb;
}

void main() {
    vec2 uv = (gl_FragCoord.xy * 2.0 - uResolution.xy) / min(uResolution.x, uResolution.y);
    float t = uTime * uSpeed * 0.5;
    
    // Interaction
    vec2 m = (uMouse * 2.0 - 1.0);
    if(uAnimType == 2) uv -= m * 0.2;
    
    // Radial coordinates
    float dist = length(uv);
    float angle = atan(uv.y, uv.x);
    
    // Create "Burst" layers
    float n = noise(uv * 1.5 + t * 0.2);
    float rays = sin(angle * 8.0 + n * 3.0 + t) * 0.5 + 0.5;
    rays *= sin(angle * 3.0 - t * 0.5) * 0.5 + 0.5;
    
    // Color mapping
    float colorIdx = dist * 0.5 + n * 0.3 - t * 0.1;
    vec3 baseCol = (uColorCount > 0) ? getCol(fract(colorIdx)) : vec3(0.5 + 0.5 * cos(t + angle + vec3(0,2,4)));
    
    // Add glow and rays
    float glow = exp(-dist * 1.5);
    vec3 finalCol = baseCol * (glow + rays * glow * 0.5);
    
    // Edge fade
    float edge = 1.0 - smoothstep(0.7, 1.2, dist);
    
    fragColor = vec4(finalCol * uIntensity * edge, 1.0);
}
`;

const hexToRgb = hex => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
    return { r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255 };
};

const PrismaticBurst = ({
    intensity = 2,
    speed = 0.5,
    animationType = 'rotate3d',
    colors,
    color0,
    color1,
    color2,
    distort = 0,
    paused = false,
    offset = { x: 0, y: 0 },
    hoverDampness = 0,
    rayCount,
    mixBlendMode = 'lighten'
}) => {
    const containerRef = useRef(null);
    const programRef = useRef(null);
    const rendererRef = useRef(null);
    const mouseTargetRef = useRef([0.5, 0.5]);
    const mouseSmoothRef = useRef([0.5, 0.5]);
    const isVisibleRef = useRef(true);
    const timeRef = useRef(0);
    const lastTimeRef = useRef(0);
    const rafRef = useRef(0);

    const colorsMemo = useMemo(() => JSON.stringify({ colors, color0, color1, color2 }), [colors, color0, color1, color2]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const renderer = new Renderer({ dpr: 1, alpha: true, antialias: false });
        rendererRef.current = renderer;

        const gl = renderer.gl;
        gl.canvas.style.position = 'absolute';
        gl.canvas.style.inset = '0';
        gl.canvas.style.width = '100%';
        gl.canvas.style.height = '100%';
        gl.canvas.style.mixBlendMode = mixBlendMode || 'lighten';
        container.appendChild(gl.canvas);

        const gradientTex = new Texture(gl, { image: new Uint8Array([255, 255, 255, 255]), width: 1, height: 1 });

        const program = new Program(gl, {
            vertex: vertexShader,
            fragment: fragmentShader,
            uniforms: {
                uResolution: { value: [1, 1] },
                uTime: { value: 0 },
                uIntensity: { value: intensity },
                uSpeed: { value: speed },
                uAnimType: { value: 1 },
                uMouse: { value: [0.5, 0.5] },
                uColorCount: { value: 0 },
                uDistort: { value: distort },
                uGradient: { value: gradientTex },
            }
        });
        programRef.current = program;

        const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

        const resize = () => {
            const w = container.clientWidth || 1;
            const h = container.clientHeight || 1;
            renderer.setSize(w, h);
            program.uniforms.uResolution.value = [gl.drawingBufferWidth, gl.drawingBufferHeight];
        };

        const ro = new ResizeObserver(resize);
        ro.observe(container);
        resize();

        const onPointer = e => {
            const rect = container.getBoundingClientRect();
            mouseTargetRef.current = [(e.clientX - rect.left) / rect.width, 1.0 - (e.clientY - rect.top) / rect.height];
        };
        window.addEventListener('pointermove', onPointer, { passive: true });

        const update = (now) => {
            rafRef.current = requestAnimationFrame(update);
            if (document.hidden || paused) {
                lastTimeRef.current = now;
                return;
            }

            const dt = (now - (lastTimeRef.current || now)) * 0.001;
            lastTimeRef.current = now;
            timeRef.current += dt;

            mouseSmoothRef.current[0] += (mouseTargetRef.current[0] - mouseSmoothRef.current[0]) * 0.1;
            mouseSmoothRef.current[1] += (mouseTargetRef.current[1] - mouseSmoothRef.current[1]) * 0.1;

            program.uniforms.uTime.value = timeRef.current;
            program.uniforms.uMouse.value = mouseSmoothRef.current;
            renderer.render({ scene: mesh });
        };
        rafRef.current = requestAnimationFrame(update);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener('pointermove', onPointer);
            ro.disconnect();
            if (gl.canvas.parentNode) container.removeChild(gl.canvas);
            gl.getExtension('WEBGL_lose_context')?.loseContext();
        };
    }, []);

    useEffect(() => {
        if (!programRef.current) return;
        programRef.current.uniforms.uIntensity.value = intensity;
        programRef.current.uniforms.uSpeed.value = speed;
        programRef.current.uniforms.uDistort.value = distort;
        const typeMap = { rotate: 0, rotate3d: 1, hover: 2 };
        programRef.current.uniforms.uAnimType.value = typeMap[animationType] || 0;
    }, [intensity, speed, animationType, distort]);

    useEffect(() => {
        if (!programRef.current || !rendererRef.current) return;
        const gl = rendererRef.current.gl;
        const tex = programRef.current.uniforms.uGradient.value;
        const finalColors = [];
        if (color0) finalColors.push(color0);
        if (color1) finalColors.push(color1);
        if (color2) finalColors.push(color2);
        if (Array.isArray(colors)) finalColors.push(...colors);

        if (finalColors.length > 0) {
            const data = new Uint8Array(finalColors.length * 4);
            finalColors.forEach((hex, i) => {
                const rgb = hexToRgb(hex);
                data[i * 4] = rgb.r; data[i * 4 + 1] = rgb.g; data[i * 4 + 2] = rgb.b; data[i * 4 + 3] = 255;
            });
            tex.image = data;
            tex.width = finalColors.length;
            tex.height = 1;
            tex.needsUpdate = true;
            programRef.current.uniforms.uColorCount.value = finalColors.length;
        }
    }, [colorsMemo]);

    return <div className="prismatic-burst-container" ref={containerRef} />;
};

export default PrismaticBurst;
